/**
 * Custom hook for managing project-scoped roles
 * 
 * Project roles are separate from global RBAC roles.
 * Each project has its own set of roles that can be assigned to members.
 */
import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { useAuth } from "contexts/AuthContext";
import {
  listProjectRoles,
  createProjectRole,
  updateProjectRole,
  deleteProjectRole,
} from "services/pmService";

/**
 * Hook for managing project roles
 * @param {string} projectId - The project ID
 * @returns {Object} - { roles, loading, error, fetchRoles, createRole, updateRole, deleteRole }
 */
export default function useProjectRoles(projectId) {
  const { user } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Handle axios errors
  const handleAxiosErrors = useCallback((err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    setError(message);
    toast.error(message);
    console.error(`${action} error:`, err);
  }, []);

  // Fetch all roles for the project
  const fetchRoles = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listProjectRoles(projectId);
      setRoles(data || []);
    } catch (error) {
      handleAxiosErrors(error, "fetch project roles");
    } finally {
      setLoading(false);
    }
  }, [projectId, handleAxiosErrors]);

  // Create a new role
  const createRole = useCallback(
    async (payload) => {
      if (!projectId) return null;
      setLoading(true);
      setError(null);
      try {
        const newRole = await createProjectRole(projectId, payload, user?.id);
        setRoles((prev) => [...prev, newRole]);
        toast.success("Role created successfully");
        return newRole;
      } catch (error) {
        handleAxiosErrors(error, "create project role");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [projectId, handleAxiosErrors, user?.id]
  );

  // Update an existing role
  const updateRole = useCallback(
    async (roleId, payload) => {
      if (!projectId || !roleId) return null;
      setLoading(true);
      setError(null);
      try {
        const updatedRole = await updateProjectRole(projectId, roleId, payload, user?.id);
        setRoles((prev) =>
          prev.map((role) => (role.id === roleId ? updatedRole : role))
        );
        toast.success("Role updated successfully");
        return updatedRole;
      } catch (error) {
        handleAxiosErrors(error, "update project role");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [projectId, handleAxiosErrors, user?.id]
  );

  // Delete a role (blocked if members are assigned)
  const removeRole = useCallback(
    async (roleId) => {
      if (!projectId || !roleId) return false;
      setLoading(true);
      setError(null);
      try {
        await deleteProjectRole(projectId, roleId, user?.id);
        setRoles((prev) => prev.filter((role) => role.id !== roleId));
        toast.success("Role deleted successfully");
        return true;
      } catch (error) {
        // Check for conflict (role has members assigned)
        if (error.response?.status === 409) {
          toast.error("Cannot delete role: members are still assigned to this role");
        } else {
          handleAxiosErrors(error, "delete project role");
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [projectId, handleAxiosErrors, user?.id]
  );

  // Auto-fetch roles when projectId changes
  useEffect(() => {
    if (projectId) {
      fetchRoles();
    }
  }, [projectId, fetchRoles]);

  return {
    roles,
    loading,
    error,
    fetchRoles,
    createRole,
    updateRole,
    removeRole,
  };
}
